import { sum } from '../../../utils/arrayUtils';
import { reactive, computed, watch } from 'vue';
import { getAllUrlParams } from '../../../utils/windowUtils';
import { useAssortmentMailSummary } from './assortmentSummary';
import { useAssortmentNetwork } from './assortmentNetwork';
import { useAssortmentIp } from './assortmentIp';

const decorateCpuSpecs = (cpu) => {
    return `${cpu.cores} Cores, ${cpu.threads} Threads, ${cpu.frequency} GHz`;
};

export function useAssortmentDedicated(
    assortmentContent,
    assortmentNetworkContent,
    assortmentIpContent
) {
    const { uplinkAssortment, trafficAssortment } = useAssortmentNetwork(assortmentNetworkContent);
    const {
        ipV4NetworkAssortment,
        ipV6NetworkAssortment,
        ipV4AddressAssortment,
        getNumberOfPayedIpv4Addresses,
    } = useAssortmentIp(assortmentIpContent);

    const sortedTrafficAssortment = Object.entries(trafficAssortment)
        .sort(([, a], [, b]) => a.volume - b.volume)
        .map(([, value]) => value);
    const defaultVolumeId = parseInt(sortedTrafficAssortment[0]?.id);
    const fields = reactive({
        deviceId: null,
        ramCapacity: null,
        storageIds: {},
        gpuIds: {},

        uplinkId: null,
        volumeId: isNaN(defaultVolumeId) ? null : defaultVolumeId,
        ipv4Addresses: 1,
        ipv4Subnet: null, // TODO: change to ipv4SubnetId when all configurators are updated
        ipv6Subnet: null, // TODO: change to ipv6SubnetId when all configurators are updated
    });

    const assortment = reactive({
        devices: [],

        uplinks: [],
        volumes: [],
        ipv4Address: {},
        ipv4Subnets: [],
        ipv6Subnets: [],
    });

    const selection = computed(() => {
        const device = assortment.devices.find(
            (entry) => String(deviceConfiguratorId(entry)) === String(fields.deviceId)
        );

        let storageQuantities = [];
        Object.entries(fields.storageIds).forEach(([id, quantity]) => {
            if (quantity === 0) {
                return;
            }
            const storageQuantity = {
                quantity,
                storage: device?.components.storage[0].options.find(
                    (storage) => String(storage.id) === String(id)
                ),
            };
            if (storageQuantity.storage) {
                storageQuantities.push(storageQuantity);
            }
        });
        storageQuantities = storageQuantities.sort((a, b) => a.storage.sort - b.storage.sort);

        const gpuQuantities = [];
        if (
            device?.components.gpu &&
            device?.components.gpu.length &&
            device?.components.gpu[0].options.length
        ) {
            Object.entries(fields.gpuIds).forEach(([id, quantity]) => {
                if (quantity === 0) {
                    return;
                }
                const gpuQuantity = {
                    quantity,
                    gpu: device?.components.gpu[0].options.find(
                        (gpu) => String(gpu.id) === String(id)
                    ),
                };
                if (gpuQuantity.gpu) {
                    gpuQuantities.push(gpuQuantity);
                }
            });
        }

        return {
            device: device,
            ram: device?.components.ram[0].options.find(
                (ram) => String(ram.capacity) === String(fields.ramCapacity)
            ),
            uplink: device?.uplinks
                ? Object.values(device.uplinks).find(
                      (uplink) => String(uplink.id) === String(fields.uplinkId)
                  )
                : undefined,
            storages: storageQuantities,
            gpus: gpuQuantities,
            ethernetControllers: device?.defaults?.['ethernet-controller'] ?? [],
            cpus: device?.defaults?.['cpu'] ?? [],

            volume: assortment.volumes[fields.volumeId],
            ipv4Addresses: fields.ipv4Addresses,
            ipv4Subnet: assortment.ipv4Subnets[fields.ipv4Subnet] || null,
            ipv6Subnet: assortment.ipv6Subnets[fields.ipv6Subnet] || null,
        };
    });

    const numberOfPayedIpv4Addresses = computed(() =>
        getNumberOfPayedIpv4Addresses(selection.value.ipv4Addresses)
    );

    const monthlyPrices = computed(() => {
        const prices = {};

        const device = selection.value.device;
        prices.device = device?.price?.monthly_price ?? 0;

        prices.ram = selection.value.ram?.monthly_price ?? 0;
        prices.storages = selection.value.storages
            .map(
                (storageQuantity) =>
                    storageQuantity.storage?.price?.monthly_price * storageQuantity.quantity
            )
            .reduce((a, b) => a + b, 0);
        prices.gpus = selection.value.gpus
            .map((gpuQuantity) => gpuQuantity.gpu?.price?.monthly_price * gpuQuantity.quantity)
            .reduce((a, b) => a + b, 0);

        prices.ethernetControllers = selection.value.ethernetControllers
            .map(
                (ethernetController) =>
                    (ethernetController.price?.monthly_price ?? 0) *
                    (ethernetController.quantity ?? 1)
            )
            .reduce((a, b) => a + b, 0);

        prices.cpus = selection.value.cpus
            .map((cpus) => (cpus.price?.monthly_price ?? 0) * (cpus.quantity ?? 1))
            .reduce((a, b) => a + b, 0);

        prices.uplink = selection.value.uplink?.monthly_price ?? 0;
        prices.volume = selection.value.volume?.monthly_price ?? 0;

        prices.ipv4Addresses = numberOfPayedIpv4Addresses.value * assortment.ipv4Address.price;
        prices.ipv4Subnet = selection.value.ipv4Subnet?.monthly_price ?? 0;
        prices.ipv6Subnet = selection.value.ipv6Subnet?.monthly_price ?? 0;

        return prices;
    });

    const monthlyPrice = computed(() => {
        return sum(Object.values(monthlyPrices.value));
    });

    const setupPrices = computed(() => {
        const prices = {};

        prices.uplink = selection.value.uplink?.setup_price ?? 0;
        prices.volume = selection.value.volume?.setup_price ?? 0;
        prices.ipv4Addresses = numberOfPayedIpv4Addresses.value * assortment.ipv4Address.setupPrice;
        prices.ipv4Subnet = selection.value.ipv4Subnet?.setup_price ?? 0;
        prices.ipv6Subnet = selection.value.ipv6Subnet?.setup_price ?? 0;

        prices.ethernetControllers = selection.value.ethernetControllers
            .map(
                (ethernetController) =>
                    (ethernetController.price?.setup_price ?? 0) *
                    (ethernetController.quantity ?? 1)
            )
            .reduce((a, b) => a + b, 0);

        prices.cpus = selection.value.cpus
            .map((cpus) => (cpus.price?.setup_price ?? 0) * (cpus.quantity ?? 1))
            .reduce((a, b) => a + b, 0);

        return prices;
    });

    const setupPrice = computed(() => {
        return sum(Object.values(setupPrices.value));
    });

    const initAssortment = (deviceAssortment) => {
        if (!deviceAssortment) return;

        assortment.devices = deviceAssortment.sort((a, b) => a.sort - b.sort);
        assortment.devices.forEach((device) => {
            if (device?.components?.ram?.[0]?.options) {
                device.components.ram[0].options = device.components.ram[0].options.sort(
                    (a, b) => a.sort - b.sort
                );
            }
            if (device?.uplinks) {
                device.uplinks = device.uplinks
                    .map((uplinkId) => {
                        return uplinkAssortment[uplinkId];
                    })
                    .sort((a, b) => a.max_volume - b.max_volume);
            }
        });

        assortment.uplinks = uplinkAssortment;
        assortment.volumes = trafficAssortment;

        assortment.ipv4Address = ipV4AddressAssortment;
        assortment.ipv4Subnets = ipV4NetworkAssortment;
        assortment.ipv6Subnets = ipV6NetworkAssortment;
    };

    const initDeviceDefaultFields = (device) => {
        if (!device) return;

        fields.ramCapacity = device.defaults?.ram?.[0]?.capacity;
        fields.uplinkId = device.uplinks?.[0]?.id;

        fields.storageIds = {};
        device.defaults?.storage?.forEach((storage) => {
            fields.storageIds[storage.id] = storage.quantity;
        });

        fields.gpuIds = {};
        if (device.defaults?.gpu) {
            device.defaults.gpu.forEach((gpu) => {
                fields.gpuIds[gpu.id] = gpu.quantity;
            });
        }
    };

    function deviceConfiguratorId(device) {
        return device?.configuration_id ?? device?.id;
    }

    const initFields = (device) => {
        fields.deviceId = deviceConfiguratorId(device);
        initDeviceDefaultFields(device);
    };

    const initFieldsByUrl = () => {
        const urlParams = getAllUrlParams();
        if (urlParams.deviceId) {
            fields.deviceId = parseInt(urlParams.deviceId);
        }
        if (urlParams.ramCapacity) {
            fields.ramCapacity = parseInt(urlParams.ramCapacity);
        }

        if (urlParams.storages) {
            fields.storageIds = {};
            Object.entries(urlParams.storages).forEach((storageEntry) => {
                const storageId = storageEntry[0];
                const quantity = parseInt(storageEntry[1]);
                if (quantity === 0) {
                    return;
                }
                fields.storageIds[storageId] = quantity;
            });
        }

        if (urlParams.gpus) {
            fields.gpuIds = {};
            Object.entries(urlParams.gpus).forEach((gpuEntry) => {
                const gpuId = gpuEntry[0];
                const quantity = parseInt(gpuEntry[1]);
                if (quantity === 0) {
                    return;
                }
                fields.gpuIds[gpuId] = quantity;
            });
        }

        if (urlParams.uplinkId) {
            fields.uplinkId = parseInt(urlParams.uplinkId);
        }
        if (urlParams.volumeId) {
            fields.volumeId = parseInt(urlParams.volumeId);
        }

        if (urlParams.ipv4Addresses) {
            fields.ipv4Addresses = parseInt(urlParams.ipv4Addresses);
            fields.ipv4Subnet = null;
            fields.ipv6Subnet = null;
        }
        if (urlParams.ipv4Subnet) {
            fields.ipv4Subnet = parseInt(urlParams.ipv4Subnet);
            fields.ipv4Addresses = 0;
        }
        if (urlParams.ipv6Subnet) {
            fields.ipv6Subnet = parseInt(urlParams.ipv6Subnet);
            fields.ipv4Addresses = 0;
        }
    };

    const mailSummary = computed(() => {
        const summaries = {};
        summaries.device = `Device: ${selection.value.device?.name} (${selection.value.device?.cpu?.name})`;
        summaries.ram = `RAM: ${selection.value.ram?.capacity} GB`;

        const storageQuantitiesummaries = selection.value.storages?.map((storageQuantity) => {
            return `${storageQuantity.quantity}x ${storageQuantity.storage.name}`;
        });
        summaries.storages = `Storages: ${storageQuantitiesummaries.join(', ')}`;

        const gpuQuantitiesummaries = selection.value.gpus?.map((gpuQuantity) => {
            return `${gpuQuantity.quantity}x ${gpuQuantity.gpu.name}`;
        });
        summaries.gpus = `GPUs: ${gpuQuantitiesummaries.join(', ')}`;

        const { networkMailSummary, ipMailSummary } = useAssortmentMailSummary(selection);
        summaries.network = networkMailSummary.value;
        summaries.ip = ipMailSummary.value;

        return Object.values(summaries).join('\n');
    });

    const usedStorageSlots = computed(() => {
        let usedSlots = 0;
        for (const id in selection.value.storages) {
            usedSlots += selection.value.storages[id].quantity;
        }
        return usedSlots;
    });

    const freeStorageSlots = computed(() => {
        return selection.value.device?.components.storage[0].size - usedStorageSlots.value;
    });

    const usedGpuSlots = computed(() => {
        let usedSlots = 0;
        for (const id in selection.value.gpus) {
            usedSlots += selection.value.gpus[id].quantity;
        }
        return usedSlots;
    });

    const freeGpuSlots = computed(() => {
        return selection.value.device?.components.gpu[0].size - usedGpuSlots.value;
    });

    const storagesByType = computed(() => {
        return groupAndSortStorages(selection.value.device.components.storage[0].options);
    });

    const groupAndSortStorages = (storages) => {
        const storagesByType = {};
        storages.forEach((storage) => {
            if (storagesByType[storage.type] === undefined) {
                storagesByType[storage.type] = [];
            }
            storagesByType[storage.type].push(storage);
        });
        Object.entries(storagesByType).forEach(([type, storages]) => {
            storagesByType[type] = storages.sort((a, b) => a.sort - b.sort);
        });
        return storagesByType;
    };

    const pushDataToUrl = () => {
        const params = new URLSearchParams(window.location.search);

        Object.entries(fields).forEach(([key, value]) => {
            if (key === 'storageIds') {
                Object.entries(value).forEach(([key, value]) => {
                    params.set(`storages[${key}]`, value);
                });
            } else if (key === 'gpuIds') {
                Object.entries(value).forEach(([key, value]) => {
                    params.set(`gpus[${key}]`, value);
                });
            } else if (value) {
                params.set(key, String(value));
            }
        });

        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    };

    watch(
        () => fields,
        (newValue) => {
            pushDataToUrl(newValue);
        },
        { deep: true }
    );

    watch(
        () => fields.deviceId,
        (newValue, oldValue) => {
            if (oldValue !== null) {
                initDeviceDefaultFields(selection.value.device);
            }
        }
    );

    const deviceAssortment = assortmentContent;

    initAssortment(deviceAssortment);
    initFields(deviceAssortment[0]);
    initFieldsByUrl();

    return {
        assortment,
        fields,
        selection,
        decorateCpuSpecs,
        storagesByType,
        usedStorageSlots,
        freeStorageSlots,
        freeGpuSlots,
        numberOfPayedIpv4Addresses,
        monthlyPrices,
        monthlyPrice,
        setupPrices,
        setupPrice,
        mailSummary,
        pushDataToUrl,
    };
}

export function useAssortmentDedicatedUtils() {
    return {
        decorateCpuSpecs,
    };
}
